/**
 * history_page_v2.js — DAS-104 Phase 02: History 시안 V1 매핑 (시안 HistoryDesktopV1 + HistoryMobileV1)
 *
 * 원본: /tmp/design-pkg/mydashboard/project/pages/history.jsx
 * 매핑: HistoryDesktopV1 + HistoryMobileV1 → 이 파일 (desktop + mobile 반응형)
 *
 * UI 구성 (시안 HistoryDesktopV1 기준 — 4개 독립 .c 카드 스택):
 *   1. 조회 범위 pill row (7일/30일/3개월/YTD/전체 + 기간 지정/CSV)
 *   2. 잔고 추이(KRW) — 차트 + time-range pill (1H/1D/1W/1M/3M/6M/1Y)
 *   3. 이벤트 내역 — 전략별 상세 + 타입 필터 pill
 *   4. 월별 손익 요약 — FE 집계 + MonthlySummaryTable
 *   5. 환율 추이 — placeholder (BE 미제공)
 *
 * 시안 ↔ 운영 매핑:
 *   - 시안 PnlChart          → TotalBalanceChart (운영 컴포넌트)
 *   - 시안 ExRateChart        → placeholder (TODO DAS-104)
 *   - 시안 TimelineRow        → HistoryChart + HistoryTable (전략 탭)
 *   - 시안 월별 손익 요약 table → FE buildMonthlyBars() + MonthlySummaryTable
 *
 * BE gap:
 *   - G4 (월별 집계): FE buildMonthlyBars()로 클라이언트 집계
 *   - H3-B (진입가/체결가/슬리피지 detail strip): BE 미제공 → placeholder
 *   - H6 (환율 시계열): BE 미제공 → placeholder
 *
 * silver 토큰 전용. JetBrains Mono 유지. 하드코딩 색상(#hex) 금지.
 */

import React from 'react';
import dayjs from 'dayjs';
import NavBar from '../common/nav_bar';
import MaintenanceBanner from '../common/maintenance_banner';
import ScrollToTopButton from '../common/scroll_to_top_button';
import FilterPill from '../common/filter_pill';
import TotalBalanceChart from '../history_page/total_balance_chart';
import HistoryTable from '../history_page/history_table';
import MonthlySummaryTable from '../history_page/monthly_summary_table';
import HistoryChart from '../history_page/history_chart';
import { fetchBalanceHistory, fetchMonthlySummary } from '../../services/balanceApi';
import { getStoredUsername, requireLoginOrThrow } from '../../utils/auth';
import { getNavCollapsed, setNavCollapsed } from '../../utils/navState';
import { isMobileViewport } from '../../utils/responsive';
import { HexLogo, V2Icon, V2Badge } from '../common/claude_primitives';
import { showToast } from '../../utils/show_toast';

/* ── 숫자 포맷 헬퍼 ──────────────────────────────────────── */
const krw0 = (n) => {
    const num = Number(n) || 0;
    return '₩' + Math.round(num).toLocaleString();
};

const pctStr = (n) => {
    const num = Number(n) || 0;
    return (num >= 0 ? '+' : '') + num.toFixed(2) + '%';
};

/* ── NOTE(DAS-65-G4): FE 집계 — monthly bars ─────────────── */
const buildMonthlyBars = (balanceHistory) => {
    const monthMap = new Map();
    (balanceHistory || []).forEach((strategyArr) => {
        (strategyArr || []).forEach((item) => {
            if (!item?.created) return;
            const monthKey = dayjs(item.created).format('YYYY-MM');
            const totalBal = Number(item.total_balance || 0);
            if (!monthMap.has(monthKey)) {
                monthMap.set(monthKey, { first: totalBal, last: totalBal });
            } else {
                const cur = monthMap.get(monthKey);
                monthMap.set(monthKey, { first: cur.first, last: totalBal });
            }
        });
    });
    const months = Array.from(monthMap.keys()).sort();
    return months.map((month, i) => {
        const { first, last } = monthMap.get(month);
        const prevMonthKey = months[i - 1];
        const startBalance = prevMonthKey ? monthMap.get(prevMonthKey).last : first;
        const pnl = last - startBalance;
        const pct = startBalance !== 0 ? (pnl / startBalance) * 100 : 0;
        return { month, start: startBalance, end: last, pnl, pct, pos: pct >= 0 };
    }).reverse();
};

/* ── 카드 컨테이너 (시안 .c) ─────────────────────────────── */
function Card({ children, style }) {
    return (
        <div
            className="rounded-2xl border border-surface-border bg-surface-card shadow-card"
            style={{ padding: 18, ...style }}
        >
            {children}
        </div>
    );
}

/* ── 카드 헤더 (시안 .c-head) ────────────────────────────── */
function CardHead({ title, badge, sub, right }) {
    return (
        <div style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginBottom: 12,
            gap: 10,
            flexWrap: 'wrap',
        }}>
            <div style={{ minWidth: 0 }}>
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    fontSize: 13, fontWeight: 600,
                    letterSpacing: '-0.01em',
                    color: 'var(--color-content)',
                }}>
                    <span>{title}</span>
                    {badge}
                </div>
                {sub && (
                    <div style={{
                        marginTop: 4,
                        fontSize: 12,
                        color: 'var(--color-content-secondary)',
                    }}>
                        {sub}
                    </div>
                )}
            </div>
            {right && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    {right}
                </div>
            )}
        </div>
    );
}

/* ── 조회 범위 pill (시안 V1 상단 범위 row) ─────────────── */
const RANGE_PILLS = ['7일', '30일', '3개월', 'YTD', '전체'];

function RangePills({ activeMode, onSelect }) {
    return (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {RANGE_PILLS.map((r) => (
                <FilterPill
                    key={r}
                    active={activeMode === r}
                    onClick={() => onSelect(r)}
                    variant="solid"
                >
                    {r}
                </FilterPill>
            ))}
        </div>
    );
}

/* ── Time-range pill row (시안 V1 차트 우상단 1H/1D/... ) ── */
const TIME_RANGES = ['1H', '1D', '1W', '1M', '3M', '6M', '1Y'];

function TimeRangePills({ active, onSelect }) {
    return (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {TIME_RANGES.map((r) => (
                <FilterPill
                    key={r}
                    active={active === r}
                    onClick={() => onSelect(r)}
                    variant="solid"
                    style={{ fontSize: 11, padding: '4px 10px' }}
                >
                    {r}
                </FilterPill>
            ))}
        </div>
    );
}

/* ── 새로고침 버튼 ─────────────────────────────────────────── */
function RefreshBtn({ loading, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={loading}
            aria-label="데이터 새로고침"
            className="flex items-center gap-1 rounded-lg border border-surface-border px-2 py-1 text-xs font-medium text-content-secondary hover:bg-surface-hover disabled:opacity-50 transition-colors"
        >
            {loading ? (
                <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
            ) : (
                <V2Icon name="refresh" size={12} />
            )}
            <span className="hidden sm:inline">{loading ? '갱신 중...' : '새로고침'}</span>
        </button>
    );
}

/* ── 월별 손익 행 ─────────────────────────────────────────── */
function MonthlyRow({ m }) {
    return (
        <div style={{
            display: 'flex', alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 0',
            borderBottom: '1px solid var(--color-surface-border)',
        }}>
            <span style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 12, fontWeight: 600,
                color: 'var(--color-content-secondary)',
            }}>
                {m.month}
            </span>
            <span style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 11,
                color: m.pos ? 'var(--color-gain)' : 'var(--color-loss)',
                fontWeight: 600,
            }}>
                {m.pos ? '+' : ''}{krw0(m.pnl)}
            </span>
            <span style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 12, fontWeight: 600,
                color: m.pos ? 'var(--color-gain)' : 'var(--color-loss)',
                minWidth: 56, textAlign: 'right',
            }}>
                {pctStr(m.pct)}
            </span>
        </div>
    );
}

/* ── 스피너 ──────────────────────────────────────────────── */
function Spinner() {
    return (
        <div style={{ padding: '32px 0', textAlign: 'center' }}>
            <svg
                className="inline h-5 w-5 animate-spin text-content-muted"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
            >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
        </div>
    );
}

/* ── 데스크톱 V1 (시안 HistoryDesktopV1 매핑) ───────────── */
function HistoryDesktopV1Refreshed({ state, handlers }) {
    const {
        loading, lastUpdated, balanceHistory, monthlySummary,
        historyMode, selectedMonth, availableMonths, activeTabKey,
        rangeMode, chartTimeRange, fxTimeRange,
    } = state;
    const {
        setHistoryMode, handleMonthChange, handleMonthFetch,
        handleRefresh, setActiveTabKey, setRangeMode,
        setChartTimeRange, setFxTimeRange,
    } = handlers;

    /* NOTE(DAS-65-G4): FE 집계 */
    const monthlyBars = buildMonthlyBars(balanceHistory);
    const headerBadge = monthlyBars.length > 0
        ? <V2Badge tone={monthlyBars[0].pos ? 'gain' : 'loss'}>{pctStr(monthlyBars[0].pct)}</V2Badge>
        : null;
    const balanceSubtitle = monthlyBars.length > 0
        ? `${krw0(monthlyBars[0].start)} → ${krw0(monthlyBars[0].end)}`
        : '전략별 합산';

    /* 전략 탭 목록 */
    const tabs = (balanceHistory || [])
        .slice()
        .sort((a, b) => (b[0]?.total_balance || 0) - (a[0]?.total_balance || 0))
        .map((hist, i) => {
            const strategyName = hist[0]?.strategy_name || 'default';
            const fiatEx = hist[0]?.fiat_exchange;
            const riskyEx = hist[0]?.risky_exchange;
            const pairLabel = fiatEx && riskyEx
                ? `${fiatEx}-${riskyEx}`
                : fiatEx ? `${fiatEx} (현물)` : riskyEx ? `${riskyEx} (선물)` : 'unknown';
            return { key: String(i), label: `${strategyName} (${pairLabel})`, hist };
        });

    const resolvedActiveKey = tabs.find((t) => t.key === activeTabKey)?.key || tabs[0]?.key;
    const activeTab = tabs.find((t) => t.key === resolvedActiveKey);

    const formatLastUpdated = (date) => {
        if (!date) return '';
        const h = String(date.getHours()).padStart(2, '0');
        const m = String(date.getMinutes()).padStart(2, '0');
        const s = String(date.getSeconds()).padStart(2, '0');
        return `${h}:${m}:${s}`;
    };

    return (
        /* 시안 .app-body { gap: 20 } */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* 페이지 헤더 — 시안 TopBar 대응 */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <HexLogo size={18} stroke="var(--color-content-secondary)" accent="var(--color-brand)" />
                    <div>
                        <div style={{
                            fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase',
                            color: 'var(--color-content-muted)', fontFamily: 'JetBrains Mono, monospace',
                        }}>
                            홈 · 거래 내역
                        </div>
                        <h1 style={{
                            fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em',
                            color: 'var(--color-content)', margin: 0,
                        }}>
                            History
                        </h1>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {lastUpdated && (
                        <span style={{
                            fontFamily: 'JetBrains Mono, monospace', fontSize: 11,
                            color: 'var(--color-content-muted)',
                        }}>
                            {formatLastUpdated(lastUpdated)} 갱신
                        </span>
                    )}
                    <RefreshBtn loading={loading} onClick={handleRefresh} />
                </div>
            </div>

            {/* 시안 V1 상단 조회 범위 pill row + 기간 지정/CSV */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <RangePills activeMode={rangeMode} onSelect={setRangeMode} />
                <div style={{ flex: 1 }} />
                {/* 시안 V1: 기간 지정 / CSV 버튼 — TODO(DAS-104): 기간 지정 picker / CSV export 기능 */}
                <button
                    type="button"
                    className="flex items-center gap-1 rounded-lg border border-surface-border px-2 py-1 text-xs font-medium text-content-secondary hover:bg-surface-hover transition-colors"
                    aria-label="기간 지정"
                >
                    <V2Icon name="calendar" size={12} />
                    기간 지정
                </button>
                <button
                    type="button"
                    className="flex items-center gap-1 rounded-lg border border-surface-border px-2 py-1 text-xs font-medium text-content-secondary hover:bg-surface-hover transition-colors"
                    aria-label="CSV 다운로드"
                >
                    <V2Icon name="download" size={12} />
                    CSV
                </button>
            </div>

            {/* 운영 월 조회 필터 (기존 기능 유지) */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                <span style={{
                    fontSize: 11, color: 'var(--color-content-muted)',
                    fontFamily: 'JetBrains Mono, monospace',
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                }}>
                    조회
                </span>
                <FilterPill active={historyMode === '30d'} onClick={() => setHistoryMode('30d')} variant="solid">
                    최근 30일
                </FilterPill>
                <select
                    value={selectedMonth}
                    onChange={handleMonthChange}
                    className="rounded-md border border-surface-border bg-surface-elevated px-2 py-1 text-xs text-content-secondary focus:border-brand focus:outline-none"
                    disabled={availableMonths.length === 0}
                    aria-label="조회할 월 선택"
                >
                    {availableMonths.length === 0 && <option value="">데이터 없음</option>}
                    {availableMonths.map((month) => (
                        <option key={month} value={month}>{month}</option>
                    ))}
                </select>
                <FilterPill
                    active={historyMode === 'month'}
                    onClick={handleMonthFetch}
                    variant="solid"
                    className={availableMonths.length === 0 ? 'cursor-not-allowed opacity-50' : ''}
                >
                    월 전체 조회
                </FilterPill>
            </div>

            {/* 카드 1: 잔고 추이(KRW) — 시안 V1 첫 번째 .c */}
            <Card>
                <CardHead
                    title="잔고 추이 (KRW)"
                    badge={headerBadge}
                    sub={balanceSubtitle}
                    right={
                        <TimeRangePills active={chartTimeRange} onSelect={setChartTimeRange} />
                    }
                />
                <TotalBalanceChart balanceHistory={balanceHistory} />
            </Card>

            {/* 카드 2: 이벤트 내역 — 시안 V1 두 번째 .c */}
            <Card>
                <CardHead
                    title="상세 내역"
                    sub={tabs.length > 0 ? `전략 ${tabs.length}개 · 차트 + 잔고 테이블` : '전략 데이터 없음'}
                    right={
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {/* 시안 V1: 이벤트 타입 필터 pill */}
                            <FilterPill active variant="solid">매수</FilterPill>
                            <FilterPill active variant="solid">매도</FilterPill>
                            <FilterPill variant="solid">입출금</FilterPill>
                        </div>
                    }
                />
                {/* 전략 탭 */}
                {tabs.length > 0 && (
                    <div style={{
                        display: 'flex', gap: 8, overflowX: 'auto',
                        borderBottom: '1px solid var(--color-surface-border)',
                        paddingBottom: 10, marginBottom: 18,
                    }}>
                        {tabs.map((tab) => (
                            <FilterPill
                                key={tab.key}
                                active={tab.key === resolvedActiveKey}
                                onClick={() => setActiveTabKey(tab.key)}
                                variant="solid"
                                size="md"
                                className="whitespace-nowrap"
                            >
                                {tab.label}
                            </FilterPill>
                        ))}
                    </div>
                )}
                {activeTab && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                        <HistoryChart balanceHistory={activeTab.hist} />
                        <HistoryTable balanceHistory={activeTab.hist} />
                    </div>
                )}
                {tabs.length === 0 && !loading && (
                    <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--color-content-muted)', fontSize: 13 }}>
                        히스토리 데이터가 없습니다
                    </div>
                )}
                {loading && tabs.length === 0 && <Spinner />}
                {/* 시안 V1: 최근 N건 표기 */}
                {tabs.length > 0 && (
                    <div style={{
                        marginTop: 4,
                        fontFamily: 'JetBrains Mono, monospace',
                        fontSize: 10,
                        color: 'var(--color-content-muted)',
                        letterSpacing: '0.06em',
                    }}>
                        · 최근 50건
                    </div>
                )}
            </Card>

            {/* 카드 3: 월별 손익 요약 — 시안 V1 세 번째 .c */}
            <Card>
                <CardHead
                    title="월별 손익 요약"
                    right={monthlyBars.length > 0 && (
                        <span style={{
                            fontFamily: 'JetBrains Mono, monospace', fontSize: 11,
                            color: 'var(--color-content-muted)',
                        }}>
                            {monthlyBars.length}개월
                        </span>
                    )}
                />
                {/* NOTE(DAS-65-G4): FE 집계 — 최근 6개월 */}
                {monthlyBars.length > 0 && (
                    <div style={{
                        marginBottom: 16,
                        padding: '12px 16px',
                        borderRadius: 12,
                        background: 'var(--color-surface-elevated)',
                        border: '1px solid var(--color-surface-border)',
                    }}>
                        <div style={{
                            fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
                            color: 'var(--color-content-muted)', fontFamily: 'JetBrains Mono, monospace',
                            marginBottom: 8, fontWeight: 500,
                        }}>
                            FE 집계 (최근 6개월)
                        </div>
                        {monthlyBars.slice(0, 6).map((m) => (
                            <MonthlyRow key={m.month} m={m} />
                        ))}
                    </div>
                )}
                {/* 운영 BE 월별 요약 테이블 */}
                <MonthlySummaryTable balanceHistory={balanceHistory} mode="total" summary={monthlySummary} />
            </Card>

            {/* 카드 4: 환율 추이 — 시안 V1 네 번째 .c */}
            <Card>
                <CardHead
                    title="환율 추이"
                    sub="잔고 차트의 환율 시리즈를 토글하여 함께 확인하세요"
                    right={
                        <div style={{ display: 'flex', gap: 4 }}>
                            {/* 시안 V1 time-range pill (1H/1D/1W/1M) */}
                            {['1H', '1D', '1W', '1M'].map((r) => (
                                <FilterPill
                                    key={r}
                                    active={fxTimeRange === r}
                                    onClick={() => setFxTimeRange(r)}
                                    variant="solid"
                                    style={{ fontSize: 11, padding: '4px 10px' }}
                                >
                                    {r}
                                </FilterPill>
                            ))}
                        </div>
                    }
                />
                <div style={{
                    padding: '20px 0',
                    fontSize: 12,
                    color: 'var(--color-content-muted)',
                    fontFamily: 'JetBrains Mono, monospace',
                    textAlign: 'center',
                    borderTop: '1px dashed var(--color-surface-border)',
                }}>
                    {/* NOTE(DAS-104-H6): 환율 시계열 BE 미제공 — 잔고 차트 series 토글로 대체 */}
                    상세 차트는 위 "상세 내역"의 "환율 (USD)" 시리즈를 활성화하세요
                </div>
            </Card>
        </div>
    );
}

/* ── 모바일 V1 (시안 HistoryMobileV1 매핑) ──────────────── */
function HistoryMobileV1Refreshed({ state, handlers }) {
    const {
        loading, balanceHistory, monthlySummary,
        historyMode, selectedMonth, availableMonths, activeTabKey,
        rangeMode, chartTimeRange,
    } = state;
    const {
        setHistoryMode, handleMonthChange, handleMonthFetch,
        handleRefresh, setActiveTabKey, setRangeMode, setChartTimeRange,
    } = handlers;

    /* NOTE(DAS-65-G4): FE 집계 */
    const monthlyBars = buildMonthlyBars(balanceHistory);
    const headerBadge = monthlyBars.length > 0
        ? <V2Badge tone={monthlyBars[0].pos ? 'gain' : 'loss'}>{pctStr(monthlyBars[0].pct)}</V2Badge>
        : null;

    const tabs = (balanceHistory || [])
        .slice()
        .sort((a, b) => (b[0]?.total_balance || 0) - (a[0]?.total_balance || 0))
        .map((hist, i) => {
            const strategyName = hist[0]?.strategy_name || 'default';
            const fiatEx = hist[0]?.fiat_exchange;
            const riskyEx = hist[0]?.risky_exchange;
            const pairLabel = fiatEx && riskyEx ? `${fiatEx}-${riskyEx}` : fiatEx || riskyEx || 'unknown';
            return { key: String(i), label: `${strategyName} · ${pairLabel}`, hist };
        });

    const resolvedActiveKey = tabs.find((t) => t.key === activeTabKey)?.key || tabs[0]?.key;
    const activeTab = tabs.find((t) => t.key === resolvedActiveKey);

    return (
        /* 시안 .m-body { gap: 14 } */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* 모바일 헤더 카드 */}
            <div
                className="rounded-2xl border border-surface-border bg-surface-card shadow-card"
                style={{ padding: 16 }}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <HexLogo size={16} stroke="var(--color-content-secondary)" accent="var(--color-brand)" />
                        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-content)', letterSpacing: '-0.01em' }}>
                            History
                        </div>
                    </div>
                    <RefreshBtn loading={loading} onClick={handleRefresh} />
                </div>
                <div style={{
                    fontSize: 11, color: 'var(--color-content-muted)',
                    fontFamily: 'JetBrains Mono, monospace',
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                }}>
                    홈 · 거래 내역
                </div>
            </div>

            {/* 시안 V1 모바일 범위 pill row — overflow: auto */}
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2 }}>
                {['7일', '30일', '3M', 'YTD', '전체'].map((r) => (
                    <FilterPill
                        key={r}
                        active={rangeMode === r}
                        onClick={() => setRangeMode(r)}
                        variant="solid"
                        className="whitespace-nowrap"
                    >
                        {r}
                    </FilterPill>
                ))}
            </div>

            {/* 운영 월 조회 필터 */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <FilterPill active={historyMode === '30d'} onClick={() => setHistoryMode('30d')} variant="solid">
                    최근 30일
                </FilterPill>
                <select
                    value={selectedMonth}
                    onChange={handleMonthChange}
                    className="rounded-md border border-surface-border bg-surface-elevated px-2 py-1 text-xs text-content-secondary focus:border-brand focus:outline-none"
                    disabled={availableMonths.length === 0}
                    aria-label="조회할 월 선택"
                >
                    {availableMonths.length === 0 && <option value="">데이터 없음</option>}
                    {availableMonths.map((month) => (
                        <option key={month} value={month}>{month}</option>
                    ))}
                </select>
                <FilterPill
                    active={historyMode === 'month'}
                    onClick={handleMonthFetch}
                    variant="solid"
                    className={availableMonths.length === 0 ? 'cursor-not-allowed opacity-50' : ''}
                >
                    월 조회
                </FilterPill>
            </div>

            {/* 카드 1: 잔고 추이 — 시안 V1 모바일 첫 .c */}
            <div
                className="rounded-2xl border border-surface-border bg-surface-card shadow-card"
                style={{ padding: 14 }}
            >
                <CardHead title="잔고 추이" badge={headerBadge} />
                <TotalBalanceChart balanceHistory={balanceHistory} />
                {/* 시안 V1 모바일 time-range pill — 카드 하단 */}
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 10 }}>
                    {['1D', '1W', '1M', '3M', '1Y'].map((r) => (
                        <FilterPill
                            key={r}
                            active={chartTimeRange === r}
                            onClick={() => setChartTimeRange(r)}
                            variant="solid"
                            style={{ fontSize: 10, padding: '3px 8px' }}
                        >
                            {r}
                        </FilterPill>
                    ))}
                </div>
            </div>

            {/* 카드 2: 이벤트 내역 — 시안 V1 모바일 두 번째 .c */}
            <div
                className="rounded-2xl border border-surface-border bg-surface-card shadow-card"
                style={{ padding: 14 }}
            >
                <CardHead title="상세 내역" />
                {tabs.length > 0 && (
                    <div style={{
                        display: 'flex', gap: 6, overflowX: 'auto',
                        paddingBottom: 8, marginBottom: 12,
                        borderBottom: '1px solid var(--color-surface-border)',
                    }}>
                        {tabs.map((tab) => (
                            <FilterPill
                                key={tab.key}
                                active={tab.key === resolvedActiveKey}
                                onClick={() => setActiveTabKey(tab.key)}
                                variant="solid"
                                className="whitespace-nowrap text-xs"
                            >
                                {tab.label}
                            </FilterPill>
                        ))}
                    </div>
                )}
                {activeTab && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <HistoryChart balanceHistory={activeTab.hist} />
                        <HistoryTable balanceHistory={activeTab.hist} />
                    </div>
                )}
                {tabs.length === 0 && !loading && (
                    <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--color-content-muted)', fontSize: 12 }}>
                        히스토리 데이터가 없습니다
                    </div>
                )}
            </div>

            {/* 카드 3: 월별 요약 — 시안 V1 모바일 세 번째 .c */}
            <div
                className="rounded-2xl border border-surface-border bg-surface-card shadow-card"
                style={{ padding: 14 }}
            >
                <CardHead title="월별 요약" />
                {/* NOTE(DAS-65-G4): FE 집계 (최근 5개월) */}
                {monthlyBars.slice(0, 5).map((m) => (
                    <MonthlyRow key={m.month} m={m} />
                ))}
                {monthlyBars.length === 0 && (
                    <div style={{ fontSize: 12, color: 'var(--color-content-muted)', padding: '12px 0' }}>
                        데이터 없음
                    </div>
                )}
                {/* 운영 BE 월별 요약 테이블 */}
                <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--color-surface-border)' }}>
                    <MonthlySummaryTable balanceHistory={balanceHistory} mode="total" summary={monthlySummary} />
                </div>
            </div>
        </div>
    );
}

/* ── 메인 클래스 컴포넌트 ─────────────────────────────────── */
class HistoryPageV2 extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            collapsed: getNavCollapsed(),
            loading: false,
            lastUpdated: null,
            balanceHistory: [],
            activeTabKey: null,
            historyMode: '30d',
            selectedMonth: dayjs().format('YYYY-MM'),
            monthlySummary: [],
            availableMonths: [],
            isMobile: isMobileViewport(),
            /* 시안 V1 UI 상태 */
            rangeMode: '7일',         /* 상단 범위 pill 선택 */
            chartTimeRange: '1M',     /* 잔고 추이 time-range */
            fxTimeRange: '1W',        /* 환율 추이 time-range */
        };
        this.isPolling = false;
        this.shouldPoll = true;
        this.pollTimer = null;
        this._onResize = this._onResize.bind(this);
    }

    _onResize() {
        const m = isMobileViewport();
        if (m !== this.state.isMobile) this.setState({ isMobile: m });
    }

    componentDidMount() {
        // eslint-disable-next-line no-restricted-syntax -- componentDidMount only
        requireLoginOrThrow();
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        window.addEventListener('resize', this._onResize);
        this.pollData();
    }

    componentWillUnmount() {
        this.shouldPoll = false;
        clearTimeout(this.pollTimer);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        window.removeEventListener('resize', this._onResize);
    }

    onVisibilityChange = () => {
        this.shouldPoll = !document.hidden;
        if (this.shouldPoll && !this.isPolling) this.pollData();
    };

    pollData = () => {
        if (!this.shouldPoll) return;
        this.isPolling = true;
        const username = getStoredUsername();
        if (!username) { this.isPolling = false; return; }
        this.setState({ loading: true });
        const { historyMode, selectedMonth } = this.state;

        const summaryPromise = fetchMonthlySummary(username)
            .then((res) => (Array.isArray(res.data) ? res.data : []))
            .catch((err) => {
                console.error('월별 요약 로드 실패', err);
                showToast({ type: 'error', message: '월별 요약 로드에 실패했습니다.' });
                return [];
            });

        summaryPromise
            .then((summary) => {
                const monthsSet = new Set();
                summary.forEach((item) => {
                    (item?.summary || []).forEach((row) => { if (row?.month) monthsSet.add(row.month); });
                });
                const availableMonths = Array.from(monthsSet).sort((a, b) => b.localeCompare(a));
                let resolvedMonth = selectedMonth;
                if (availableMonths.length > 0 && !availableMonths.includes(resolvedMonth))
                    resolvedMonth = availableMonths[0];
                if (!resolvedMonth) resolvedMonth = availableMonths.length > 0 ? availableMonths[0] : '';
                const params = historyMode === 'month'
                    ? (resolvedMonth ? { month: resolvedMonth } : {})
                    : { start_date: dayjs().subtract(30, 'day').format('YYYY-MM-DD') };

                return fetchBalanceHistory(username, params)
                    .then((historyResponse) => {
                        const history = Array.isArray(historyResponse.data) ? historyResponse.data : [];
                        this.setState({
                            balanceHistory: history,
                            monthlySummary: summary,
                            availableMonths,
                            selectedMonth: resolvedMonth,
                            lastUpdated: new Date(),
                        });
                    })
                    .catch((err) => {
                        console.error('히스토리 로드 실패', err);
                        showToast({ type: 'error', message: '히스토리 로드에 실패했습니다.' });
                        this.setState({ monthlySummary: summary, availableMonths, selectedMonth: resolvedMonth, lastUpdated: new Date() });
                    });
            })
            .finally(() => {
                this.isPolling = false;
                // 언마운트(shouldPoll=false) 후 setState 방지 — DAS-116 안전판
                if (!this.shouldPoll) return;
                this.setState({ loading: false });
                this.pollTimer = setTimeout(this.pollData, 5 * 60 * 1000);
            });
    };

    setHistoryMode = (mode) => { this.setState({ historyMode: mode }, () => { this.pollData(); }); };
    handleMonthChange = (event) => { this.setState({ selectedMonth: event.target.value }); };
    handleMonthFetch = () => {
        if (!this.state.selectedMonth) return;
        this.setState({ historyMode: 'month' }, () => { this.pollData(); });
    };
    onCollapse = (collapsed) => { setNavCollapsed(collapsed); this.setState({ collapsed }); };
    handleRefresh = () => { this.pollData(); };
    setActiveTabKey = (key) => { this.setState({ activeTabKey: key }); };
    setRangeMode = (mode) => { this.setState({ rangeMode: mode }); };
    setChartTimeRange = (r) => { this.setState({ chartTimeRange: r }); };
    setFxTimeRange = (r) => { this.setState({ fxTimeRange: r }); };

    render() {
        const { collapsed, isMobile } = this.state;

        const sharedState = { ...this.state };
        const sharedHandlers = {
            setHistoryMode: this.setHistoryMode,
            handleMonthChange: this.handleMonthChange,
            handleMonthFetch: this.handleMonthFetch,
            handleRefresh: this.handleRefresh,
            setActiveTabKey: this.setActiveTabKey,
            setRangeMode: this.setRangeMode,
            setChartTimeRange: this.setChartTimeRange,
            setFxTimeRange: this.setFxTimeRange,
        };

        return (
            <div className="min-h-screen w-full bg-surface text-content sm:overflow-x-auto">
                <NavBar collapsed={collapsed} onCollapse={this.onCollapse} />
                <main
                    className={`min-w-0 w-full pt-12 pb-16 sm:pt-0 sm:pb-0 transition-[padding-left] duration-200 ${
                        collapsed ? 'sm:pl-[56px]' : 'sm:pl-[220px]'
                    }`}
                    style={{ marginTop: 'var(--maintenance-banner-h, 0px)' }}
                >
                    <MaintenanceBanner />
                    <div className="mx-3 my-3 min-w-0 sm:mx-6 sm:my-5">
                        {isMobile
                            ? <HistoryMobileV1Refreshed state={sharedState} handlers={sharedHandlers} />
                            : <HistoryDesktopV1Refreshed state={sharedState} handlers={sharedHandlers} />
                        }
                    </div>
                </main>
                <ScrollToTopButton />
            </div>
        );
    }
}

export default HistoryPageV2;
